import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  DollarSign, PlusCircle, Search, Filter, Download, X, Copy, ExternalLink,
  CreditCard, QrCode, Zap, ChevronDown, User as UserIcon, Users, Loader2, ScrollText,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const brl = (cents = 0) => `R$ ${((cents || 0) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dOnly = (iso = "") => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso || "—"; } };
const dtBr = (iso = "") => { try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso || "—"; } };

// ---------- KPI card ----------
function Kpi({ label, value, hint, tone = "slate", testid }) {
  const tones = {
    slate: "text-slate-900",
    green: "text-emerald-700",
    amber: "text-amber-700",
    red:   "text-rose-700",
    gold:  "text-[#a37417]",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 text-3xl font-serif ${tones[tone]}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

// ---------- Cobrança modal ----------
function CobrancaModal({ open, onClose, onCreated }) {
  const [mode, setMode] = useState("picker"); // picker | manual
  const [clientQuery, setClientQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [pickedId, setPickedId] = useState("");
  const [manual, setManual] = useState({
    nome_completo: "", cpf: "", telefone: "", email: "", endereco_cidade: "", endereco_uf: "",
  });
  const [form, setForm] = useState({ amount: "", method: "card", installments: 1, description: "Honorários advocatícios" });
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/clients?page_size=200").then(r => setClients(r.data.rows || r.data || []));
    api.get("/cobrancas/available-coupons")
      .then(r => setAvailableCoupons(r.data.coupons || []))
      .catch(() => setAvailableCoupons([]));
    // reset
    setResult(null); setPickedId(""); setForm({ amount: "", method: "card", installments: 1, description: "Honorários advocatícios" });
    setManual({ nome_completo: "", cpf: "", telefone: "", email: "", endereco_cidade: "", endereco_uf: "" });
    setCouponCode("");
    setMode("picker");
  }, [open]);

  const filteredClients = useMemo(() => {
    if (!clientQuery.trim()) return clients.slice(0, 30);
    const q = clientQuery.toLowerCase();
    return clients
      .filter(c => (c.nome_completo || "").toLowerCase().includes(q) || (c.cpf || "").includes(q))
      .slice(0, 30);
  }, [clients, clientQuery]);

  if (!open) return null;

  const submit = async () => {
    if (!form.amount) return toast.error("Informe o valor");
    if (mode === "picker" && !pickedId) return toast.error("Selecione um cliente ou use o modo Digitar");
    if (mode === "manual" && !manual.nome_completo.trim()) return toast.error("Informe o nome do cliente");
    setCreating(true);
    try {
      const payload = {
        amount: form.amount,
        method: form.method,
        installments: form.method === "card" ? Number(form.installments) : 1,
        description: form.description,
        coupon_code: couponCode || undefined,
        ...(mode === "picker" ? { client_id: pickedId } : manual),
      };
      const r = await api.post("/cobrancas", payload);
      setResult(r.data);
      onCreated?.();
      toast.success("Cobrança criada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar cobrança");
    } finally { setCreating(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(result.payment_link_url); toast.success("Link copiado"); };
  const sendWa = () => {
    const snap = result.client_snapshot || {};
    const phone = (snap.telefone || "").replace(/\D/g, "");
    if (!phone) return toast.error("Cliente sem telefone");
    const num = phone.length === 11 ? `55${phone}` : phone;
    const msg = encodeURIComponent(
      `Olá, ${snap.nome_completo}! Segue seu link seguro de pagamento (${result.description}): ${result.payment_link_url}`
    );
    window.open(`https://wa.me/${num}?text=${msg}`, "_blank");
  };
  const sendEmail = () => {
    const snap = result.client_snapshot || {};
    if (!snap.email) return toast.error("Cliente sem e-mail");
    const subject = encodeURIComponent(`Cobrança — ${result.description}`);
    const body = encodeURIComponent(
      `Olá, ${snap.nome_completo}.\n\nSegue link seguro de pagamento no valor de ${brl(result.amount_cents)}:\n${result.payment_link_url}\n\nAtenciosamente.`
    );
    window.open(`mailto:${snap.email}?subject=${subject}&body=${body}`, "_blank");
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" role="dialog" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="cobranca-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Nova cobrança</div>
            <h2 className="font-heading text-xl font-semibold">Emitir cobrança</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="cobranca-close"><X className="w-5 h-5"/></button>
        </div>

        {!result ? (
          <div className="p-6 space-y-5">
            {/* Client picker */}
            <div>
              <div className="text-xs font-medium text-slate-600 mb-2">Cliente</div>
              <div className="flex gap-2 mb-3">
                <button onClick={()=>setMode("picker")} data-testid="cobranca-mode-picker"
                  className={`flex-1 h-10 rounded-lg text-sm font-medium border flex items-center justify-center gap-2 ${mode==="picker" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                  <Users className="w-4 h-4"/> Escolher da agenda
                </button>
                <button onClick={()=>setMode("manual")} data-testid="cobranca-mode-manual"
                  className={`flex-1 h-10 rounded-lg text-sm font-medium border flex items-center justify-center gap-2 ${mode==="manual" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                  <UserIcon className="w-4 h-4"/> Digitar cliente
                </button>
              </div>
              {mode === "picker" ? (
                <div>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <input value={clientQuery} onChange={e=>setClientQuery(e.target.value)}
                      placeholder="Buscar por nome ou CPF…" data-testid="cobranca-client-search"
                      className="w-full pl-9 pr-3 h-10 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-slate-900"/>
                  </div>
                  <div className="max-h-52 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100" data-testid="cobranca-client-list">
                    {filteredClients.length === 0 && <div className="p-4 text-sm text-slate-500 text-center">Nenhum cliente encontrado. Use o modo "Digitar cliente".</div>}
                    {filteredClients.map(c => (
                      <label key={c.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${pickedId === c.id ? "bg-amber-50" : ""}`}>
                        <input type="radio" name="cli" checked={pickedId === c.id} onChange={()=>setPickedId(c.id)}
                          data-testid={`cobranca-client-${c.id}`} className="accent-slate-900"/>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-900 truncate">{c.nome_completo || "(sem nome)"}</div>
                          <div className="text-[11px] text-slate-500 truncate">{c.cpf || "—"} · {c.endereco_cidade || "—"}/{c.endereco_uf || "—"}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Nome completo *" value={manual.nome_completo} onChange={e=>setManual({...manual, nome_completo: e.target.value})} testid="cobranca-nome"/>
                  <Field label="CPF" value={manual.cpf} onChange={e=>setManual({...manual, cpf: e.target.value})} testid="cobranca-cpf"/>
                  <Field label="Telefone (WhatsApp)" value={manual.telefone} onChange={e=>setManual({...manual, telefone: e.target.value})} testid="cobranca-telefone"/>
                  <Field label="E-mail" value={manual.email} onChange={e=>setManual({...manual, email: e.target.value})} testid="cobranca-email"/>
                  <Field label="Cidade" value={manual.endereco_cidade} onChange={e=>setManual({...manual, endereco_cidade: e.target.value})} testid="cobranca-cidade"/>
                  <label className="block">
                    <div className="text-xs font-medium text-slate-600 mb-1">UF</div>
                    <select value={manual.endereco_uf} onChange={e=>setManual({...manual, endereco_uf: e.target.value})}
                      className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"
                      data-testid="cobranca-uf">
                      <option value="">—</option>
                      {UFS.map(u=><option key={u} value={u}>{u}</option>)}
                    </select>
                  </label>
                  <div className="col-span-2 text-[11px] text-slate-500">O cliente digitado será criado automaticamente na sua agenda de clientes.</div>
                </div>
              )}
            </div>

            {/* Amount + description */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Valor (R$) *" value={form.amount} onChange={e=>setForm({...form, amount: e.target.value})}
                placeholder="1200,00" testid="cobranca-amount"/>
              <div className="md:col-span-2">
                <Field label="Descrição / motivo" value={form.description} onChange={e=>setForm({...form, description: e.target.value})} testid="cobranca-description"/>
              </div>
            </div>

            {/* Payment method */}
            <div>
              <div className="text-xs font-medium text-slate-600 mb-2">Forma de pagamento</div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { k: "card", l: "Cartão", i: CreditCard, hint: "Até 12x" },
                  { k: "pix",  l: "PIX",    i: Zap,        hint: "À vista" },
                  { k: "qr",   l: "QR Code",i: QrCode,     hint: "Para exibir/imprimir" },
                ].map(m => (
                  <button key={m.k} onClick={()=>setForm({...form, method: m.k})} data-testid={`cobranca-method-${m.k}`}
                    className={`text-left rounded-xl border p-3 transition ${form.method === m.k ? "border-[#d4a24c] bg-amber-50 ring-1 ring-[#d4a24c]" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="flex items-center gap-2">
                      <m.i className={`w-4 h-4 ${form.method === m.k ? "text-[#a37417]" : "text-slate-600"}`}/>
                      <span className="font-medium text-sm">{m.l}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">{m.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            {form.method === "card" && (
              <div>
                <div className="text-xs font-medium text-slate-600 mb-1">Parcelas máximas oferecidas</div>
                <div className="grid grid-cols-6 md:grid-cols-12 gap-1" data-testid="cobranca-installments">
                  {Array.from({length: 12}).map((_,i) => {
                    const n = i+1;
                    return (
                      <button key={n} onClick={()=>setForm({...form, installments: n})}
                        className={`h-9 text-xs rounded-md border ${form.installments === n ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                        data-testid={`cobranca-inst-${n}`}>{n}x</button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">Sem juros até onde a operadora permitir. As opções aparecerão para o cliente na tela de pagamento.</div>
              </div>
            )}

            {/* Pre-applied coupon */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-medium text-slate-600">Cupom pré-aplicado <span className="text-slate-400">(opcional)</span></div>
                {couponCode && (
                  <button type="button" onClick={()=>setCouponCode("")}
                    className="text-[11px] text-slate-500 hover:text-slate-900 underline"
                    data-testid="cobranca-coupon-clear">Remover</button>
                )}
              </div>
              <select
                value={couponCode}
                onChange={e=>setCouponCode(e.target.value)}
                className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-[#d4a24c] outline-none text-sm"
                data-testid="cobranca-coupon-select"
              >
                <option value="">— Nenhum cupom —</option>
                {availableCoupons.map(c => {
                  const disc = c.percent_off ? `${c.percent_off}% OFF` : (c.amount_off_cents ? `R$ ${(c.amount_off_cents/100).toFixed(2)} OFF` : "");
                  const remaining = c.unlimited
                    ? " · ilimitado"
                    : ` · ${c.remaining} uso${c.remaining === 1 ? "" : "s"} restante${c.remaining === 1 ? "" : "s"}`;
                  const label = `${c.code} — ${disc}${remaining}`;
                  const exhausted = !c.unlimited && c.remaining <= 0;
                  return (
                    <option
                      key={c.id}
                      value={c.code}
                      disabled={exhausted}
                      data-testid={`cobranca-coupon-option-${c.code}`}
                    >
                      {exhausted ? `${label} (esgotado)` : label}
                    </option>
                  );
                })}
              </select>
              {couponCode && (() => {
                const c = availableCoupons.find(x => x.code === couponCode);
                if (!c) return null;
                const low = !c.unlimited && c.remaining > 0 && c.remaining <= 3;
                return (
                  <div className={`mt-1 text-[11px] ${low ? "text-amber-700" : "text-emerald-700"}`} data-testid="cobranca-coupon-hint">
                    ✓ O cliente verá o desconto já aplicado no link (via <code>?prefilled_promo_code</code>).
                    {low && <> · <strong>Atenção:</strong> restam apenas {c.remaining} uso{c.remaining === 1 ? "" : "s"}.</>}
                  </div>
                );
              })()}
              {availableCoupons.length === 0 && (
                <div className="mt-1 text-[11px] text-slate-500">
                  Nenhum cupom ativo. Crie cupons em <span className="font-medium">Admin → Cupons</span>.
                </div>
              )}
            </div>

            <div className="pt-2 flex justify-end gap-2 border-t border-slate-100">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm">Cancelar</button>
              <button onClick={submit} disabled={creating}
                className="px-5 py-2 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] disabled:opacity-40 text-black text-sm font-semibold flex items-center gap-2"
                data-testid="cobranca-submit">
                {creating ? <><Loader2 className="w-4 h-4 animate-spin"/> Enviando…</> : <>ENVIAR cobrança</>}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-5" data-testid="cobranca-result">
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
              ✔ Cobrança criada para <strong>{result.client_snapshot?.nome_completo}</strong> —
              valor <strong>{brl(result.amount_cents)}</strong>{result.method === "card" && result.installments_max > 1 && <> · <strong>até {result.installments_max}x</strong></>}.
              {result.coupon_code && (
                <> · Cupom <strong>{result.coupon_code}</strong>{result.coupon_percent_off ? <> ({result.coupon_percent_off}% OFF)</> : null} já aplicado.</>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Link de pagamento</div>
                <div className="flex items-center gap-2">
                  <input readOnly value={result.payment_link_url}
                    className="flex-1 h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm" data-testid="cobranca-link"/>
                  <button onClick={copyLink} className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700" data-testid="cobranca-copy">
                    <Copy className="w-4 h-4"/>
                  </button>
                  <a href={result.payment_link_url} target="_blank" rel="noreferrer"
                    className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 inline-flex items-center">
                    <ExternalLink className="w-4 h-4"/>
                  </a>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button onClick={sendWa}
                    className="h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium" data-testid="cobranca-wa">
                    Enviar via WhatsApp
                  </button>
                  <button onClick={sendEmail}
                    className="h-10 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium" data-testid="cobranca-email-send">
                    Enviar por e-mail
                  </button>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">QR Code</div>
                <div className="rounded-xl border border-slate-200 p-3 flex items-center justify-center bg-white">
                  <img src={result.qr_png_data_url} alt="QR" className="w-48 h-48" data-testid="cobranca-qr"/>
                </div>
                <a href={result.qr_png_data_url} download={`cobranca-${result.id}.png`}
                  className="mt-2 text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
                  <Download className="w-3 h-3"/> Baixar QR
                </a>
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-5 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium" data-testid="cobranca-done">
                Fechar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      <input {...rest} data-testid={testid}
        className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"/>
    </label>
  );
}

// ---------- Main page ----------
export default function Cobrancas() {
  const [openModal, setOpenModal] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", method: "", state: "", city: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);

  const buildParams = (extra = {}) => {
    const p = new URLSearchParams({ page: extra.page || page, page_size: 20 });
    Object.entries({ ...filters, ...extra }).forEach(([k,v]) => { if (v) p.set(k, v); });
    return p;
  };

  const load = async () => {
    const [rowsR, anaR] = await Promise.all([
      api.get(`/cobrancas?${buildParams()}`),
      api.get(`/cobrancas/analytics?${buildParams({ page: 1 })}`),
    ]);
    setRows(rowsR.data.rows); setTotal(rowsR.data.total);
    setAnalytics(anaR.data);
  };
  useEffect(() => { load(); }, [page]); // eslint-disable-line

  const applyFilters = () => { setPage(1); load(); };
  const clearFilters = () => { setFilters({ q: "", status: "", method: "", state: "", city: "", date_from: "", date_to: "" }); setPage(1); setTimeout(load, 0); };

  const downloadReport = () => {
    const p = buildParams({ page: 1 });
    p.delete("page"); p.delete("page_size");
    const url = `${API}/cobrancas/report.xlsx?${p}`;
    window.open(url, "_blank");
  };

  const kpi = analytics?.kpis || {};
  const tsData = (analytics?.timeseries || []).map(t => ({
    day: t.day, cobrado: t.created_cents / 100, pago: t.paid_cents / 100,
  }));
  const methodData = (analytics?.by_method || []).map(m => ({
    name: { card: "Cartão", pix: "PIX", qr: "QR Code" }[m.method] || m.method,
    value: m.amount_cents / 100, count: m.count,
  }));
  const stateData = (analytics?.by_state || []).map(s => ({ uf: s.uf, valor: s.amount_cents / 100, count: s.count }));

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="cobrancas-page">
      <div className="p-3 sm:p-6 lg:p-8">
        {/* Header */}
        <header className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#a37417]">Financeiro</div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900 mt-1">Cobranças</h1>
            <p className="text-sm text-slate-500 mt-1">Emita cobranças em cartão, PIX ou QR Code, com acompanhamento em tempo real.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={downloadReport}
              className="h-11 px-4 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 text-sm font-medium flex items-center gap-2"
              data-testid="cobrancas-export">
              <Download className="w-4 h-4"/> Exportar XLSX
            </button>
            <button onClick={()=>setOpenModal(true)} data-testid="cobrancas-new-btn"
              className="h-11 px-5 rounded-xl bg-[#d4a24c] hover:bg-[#c48a3e] text-black font-semibold text-sm flex items-center gap-2 shadow-[0_6px_20px_rgba(212,162,76,0.35)]">
              <PlusCircle className="w-5 h-5"/> COBRANÇA
            </button>
          </div>
        </header>

        {/* KPIs */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Kpi label="Total em cobrança" value={brl(kpi.total_amount_cents)} hint={`${kpi.total_criadas || 0} lançada(s)`} tone="gold" testid="kpi-total"/>
          <Kpi label="Pagas" value={brl(kpi.pagas?.amount)} hint={`${kpi.pagas?.count || 0} recebida(s)`} tone="green" testid="kpi-pagas"/>
          <Kpi label="Pendentes" value={brl(kpi.pendentes?.amount)} hint={`${kpi.pendentes?.count || 0} aguardando`} tone="amber" testid="kpi-pendentes"/>
          <Kpi label="Vencidas (>7d)" value={brl(kpi.overdue?.amount)} hint={`${kpi.overdue?.count || 0} atrasada(s)`} tone="red" testid="kpi-vencidas"/>
        </section>

        {/* Filters */}
        <section className="rounded-2xl border border-slate-200 bg-white p-4 mb-6" data-testid="cobrancas-filters">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            <div className="relative md:col-span-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={filters.q} onChange={e=>setFilters({...filters, q: e.target.value})} onKeyDown={e=>e.key==='Enter' && applyFilters()}
                placeholder="Buscar por cliente, CPF ou descrição"
                className="w-full pl-9 h-10 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-slate-900"
                data-testid="filter-q"/>
            </div>
            <select value={filters.status} onChange={e=>setFilters({...filters, status: e.target.value})}
              className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm" data-testid="filter-status">
              <option value="">Status: todos</option>
              <option value="pending">Pendente</option>
              <option value="paid">Pago</option>
              <option value="cancelled">Cancelado</option>
            </select>
            <select value={filters.method} onChange={e=>setFilters({...filters, method: e.target.value})}
              className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm" data-testid="filter-method">
              <option value="">Método: todos</option>
              <option value="card">Cartão</option>
              <option value="pix">PIX</option>
              <option value="qr">QR</option>
            </select>
            <select value={filters.state} onChange={e=>setFilters({...filters, state: e.target.value})}
              className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm" data-testid="filter-state">
              <option value="">UF: todas</option>
              {UFS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <input placeholder="Cidade" value={filters.city} onChange={e=>setFilters({...filters, city: e.target.value})}
              className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm" data-testid="filter-city"/>
            <input type="date" value={filters.date_from} onChange={e=>setFilters({...filters, date_from: e.target.value})}
              className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm" data-testid="filter-from"/>
            <input type="date" value={filters.date_to} onChange={e=>setFilters({...filters, date_to: e.target.value})}
              className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm" data-testid="filter-to"/>
            <div className="md:col-span-4 flex justify-end gap-2">
              <button onClick={clearFilters} className="h-10 px-4 rounded-lg border border-slate-200 text-slate-700 text-sm hover:bg-slate-50" data-testid="filter-clear">Limpar</button>
              <button onClick={applyFilters} className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium" data-testid="filter-apply">Aplicar filtros</button>
            </div>
          </div>
        </section>

        {/* Charts */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="chart-timeseries">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Cobranças ao longo do tempo</div>
            <div className="font-semibold text-slate-900 mb-3">Cobrado × Recebido</div>
            <div className="h-64">
              {tsData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-400">Sem cobranças no período</div>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={tsData}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3"/>
                    <XAxis dataKey="day" stroke="#94a3b8" fontSize={11}/>
                    <YAxis stroke="#94a3b8" fontSize={11}/>
                    <Tooltip formatter={(v)=>new Intl.NumberFormat("pt-BR", { style:"currency", currency:"BRL" }).format(v)}/>
                    <Legend/>
                    <Line type="monotone" dataKey="cobrado" stroke="#d4a24c" strokeWidth={2.5} dot={false}/>
                    <Line type="monotone" dataKey="pago" stroke="#10b981" strokeWidth={2.5} dot={false}/>
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="chart-methods">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Distribuição por método</div>
            <div className="font-semibold text-slate-900 mb-3">Valor (R$)</div>
            <div className="h-64">
              {methodData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-400">Sem dados</div>
              ) : (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={methodData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      {methodData.map((_, i) => <Cell key={i} fill={["#d4a24c", "#0ea5e9", "#8b5cf6"][i % 3]}/>)}
                    </Pie>
                    <Tooltip formatter={(v)=>new Intl.NumberFormat("pt-BR", { style:"currency", currency:"BRL" }).format(v)}/>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>

        {stateData.length > 0 && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 mb-6" data-testid="chart-state">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Por estado</div>
            <div className="font-semibold text-slate-900 mb-3">Valor por UF</div>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={stateData}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3"/>
                  <XAxis dataKey="uf" stroke="#94a3b8" fontSize={11}/>
                  <YAxis stroke="#94a3b8" fontSize={11}/>
                  <Tooltip formatter={(v)=>new Intl.NumberFormat("pt-BR", { style:"currency", currency:"BRL" }).format(v)}/>
                  <Bar dataKey="valor" fill="#d4a24c" radius={[6,6,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {/* Transactions table */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 mb-6" data-testid="cobrancas-table">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Detalhamento</div>
              <div className="font-semibold text-slate-900">{total} cobrança(s) no filtro atual</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                  <th className="py-3 pr-4">Data</th>
                  <th className="py-3 pr-4">Cliente</th>
                  <th className="py-3 pr-4">Local</th>
                  <th className="py-3 pr-4">Método</th>
                  <th className="py-3 pr-4">Descrição</th>
                  <th className="py-3 pr-4 text-right">Valor</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan="8" className="py-10 text-center text-slate-400">Nenhuma cobrança encontrada</td></tr>}
                {rows.map(r => {
                  const snap = r.client_snapshot || {};
                  const status = r.payment_status || "pending";
                  const pill = status === "paid" ? "bg-emerald-100 text-emerald-700"
                             : status === "cancelled" ? "bg-slate-100 text-slate-600"
                             : "bg-amber-100 text-amber-700";
                  return (
                    <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`cobranca-row-${r.id}`}>
                      <td className="py-3 pr-4 text-slate-500">{dtBr(r.created_at)}</td>
                      <td className="py-3 pr-4">
                        <div className="text-slate-900">{snap.nome_completo || "—"}</div>
                        <div className="text-[11px] text-slate-500">{snap.cpf || "—"}</div>
                      </td>
                      <td className="py-3 pr-4 text-slate-600 text-xs">{snap.endereco_cidade || "—"}/{snap.endereco_uf || "—"}</td>
                      <td className="py-3 pr-4">
                        <span className="inline-flex items-center gap-1 text-xs">
                          {r.method === "card" ? <><CreditCard className="w-3 h-3"/> Cartão{r.installments_max > 1 && <> ({r.installments_max}x)</>}</>
                            : r.method === "pix" ? <><Zap className="w-3 h-3"/> PIX</>
                            : <><QrCode className="w-3 h-3"/> QR</>}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-slate-600 text-xs truncate max-w-[220px]">{r.description}</td>
                      <td className="py-3 pr-4 text-right font-semibold text-slate-900">{brl(r.amount_cents)}</td>
                      <td className="py-3 pr-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${pill}`}>
                          {status === "paid" ? "Pago" : status === "cancelled" ? "Cancelado" : "Pendente"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <a href={r.payment_link_url} target="_blank" rel="noreferrer"
                          className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 text-xs"
                          data-testid={`cobranca-open-${r.id}`}>
                          Abrir <ExternalLink className="w-3 h-3"/>
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <CobrancaModal open={openModal} onClose={()=>setOpenModal(false)} onCreated={load}/>
    </div>
  );
}
